/**
 * @class
 * @extends Netx.Model
 */
import AttributeProfiles from '@netx/core-asset/collections/attributeProfiles';
import AssetModel from '@netx/core-asset/models/asset';
import AssetSetModel from '@netx/core-asset/models/assetSet';
import AttributeProfile from '@netx/core-asset/models/attributeProfile';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
// Portal can't handle this import - using the global that is needed in other spots as well
// import eventProgressController from '@netx/core-event-progress/controller';
import {
	getObjectIdFromProgressReport,
	pollUntil,
} from '@netx/core-next/utils/poll';
import AssetSearhRulesCollection from '@netx/core-search/collections/searchRules_assets';
import Base from '@netx/core/lib/netx-model';
import CategoryTreeNodesCollection from '../collections/categoryTreeNodes';
import customConfig from '../config';
import CategoryDetailsData from './categoryDetailsData';
import { CATEGORY_EVENT } from '@netx/core-category-next/actions/constants';

export default class Category extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'categoryid';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} defaults - Default attributes
	 * @default
	 * @property {number}  defaults.assetId             - Primary asset for category (id)
	 * @property {string}  defaults.assetLabel          - Primary asset for category (label)
	 * @property {string}  defaults.byteCount           - Size of all assets in category
	 * @property {array}   defaults.categoryAttributes  - Attributes for category (an array of ids)
	 * @property {number}  defaults.categoryid          - Category id (Never set the id in defaults)
	 * @property {array}   defaults.children            - Child categories (an array of ids)
	 * @property {string}  defaults.contents            - ??? Generally is filled with thie string "categories"
	 * @property {string}  defaults.creationDate        - Date created
	 * @property {string}  defaults.description         - Description
	 * @property {boolean} defaults.facet               - Determines whether a category should be used as a facet
	 * @property {string}  defaults.file                - Primary file path for category
	 * @property {number}  defaults.fileCount 			- Number of assets in category
	 * @property {boolean} defaults.hasContents         - Whether or not the category has any assets
	 * @property {string}  defaults.moduserlabel        - Last user to modify category
	 * @property {string}  defaults.name                - Category name
	 * @property {number}  defaults.orderby             - Place in order
	 * @property {string}  defaults.parentCategoryName  - Parent category name
	 * @property {number}  defaults.parentid            - Parent id
	 * @property {string}  defaults.path                - Path to category
	 * @property {array}   defaults.permissionMatrix    - Permission matrix ['read', 'download', 'add', 'modify', 'delete', 'all'] (represented by 1s and 0s)
	 * @property {number}  defaults.repositorydirectory - Repository directory (eg "cat1/cat2/cat3")
	 * @property {number}  defaults.subCategoryCount    - Numbers of categories within parent category
	 * @property {number}  defaults.type                - ??? Generally is filled wit the number 1
	 * @property {number}  defaults.userId              - Id of user who created category
	 * @property {number}  defaults.visible             - Whether or not the category iss visible (a boolean represented by a number)
	 */
	get defaults() {
		return {
			assetId: 0,
			assetLabel: '',
			byteCount: '',
			categoryAttributes: [],
			// categoryid: 0,
			children: [],
			contents: '',
			creationDate: '',
			description: '',
			facet: false,
			file: '',
			fileCount: 0,
			hasContents: false,
			moduserlabel: '',
			name: '',
			orderby: 0,
			parentCategoryName: '',
			parentid: 1,
			path: '',
			permissionMatrix: [],
			repositorydirectory: '/',
			subCategoryCount: 0,
			type: 0,
			userId: 0,
			visible: 1,

			// Client attributes
			open: false,
			breadcrumb: [],
			// tree: new Netx.Collections.CategoryTreeNodes([]),
			// childCategories: new Netx.Collections.CategoryTreeNodes([])
		};
	}

	// Attributes that must be removed before sending to server
	get clientAttrs() {
		return [
			'breadcrumb',
			'byteCount',
			'childCategories',
			'fileCount',
			'open',
			'subCategoryCount',
			'tree',
			'collection',
		];
	}

	/**
	 * @property {object} methods                         - Methods for this model
	 * @property {method} methods.create                  - Create category
	 * @property {method} methods.read                    - Fetch category
	 * @property {method} methods.remove                  - Destroy category
	 * @property {method} methods.update                  - Update category
	 *
	 * @property {method} methods._addToCategory          - Add assets to category
	 * @property {method} methods.fetchChildren           - Fetch category children
	 * @property {method} methods.getCategoryBreadcrumbs  - Fetch category breadcrumbs
	 * @property {method} methods.getCategoryByPath       - Fetch category by path string
	 * @property {method} methods.getCategoryData         - Fetch category
	 * @property {method} methods.getCategoryDetailsData  - Fetch category details data
	 * @property {method} methods._moveToCategory         - Move assets to category
	 * @property {method} methods.setCategoryObject       - Update category
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create: function (opts) {
				// The server really wants this id to be passed at creation...
				this.set('categoryid', 0, {silent: true});
				return this.setCategoryObject(opts).then((id) => {
					return this.set(
						{
							categoryid: id,
						},
						opts,
					).fetch(opts);
				});
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read: function (opts) {
				var d = new $.Deferred();

				this.getCategoryData(
					_.extend({duplicateQueryPolicy: 'abort'}, opts),
				).then(function (category) {
					// TODO: See https://jira.netx.net/browse/DAM-9247
					// The server returns an empty category with the id of 0 when one is not found
					if (category.categoryid === 0) {
						return d.reject(new Error(i18n.t('i18n.categoryNotFound')));
					}
					if (!opts.silent) {
						// Open
						this.set(
							'open',
							this.get('open') && this.get('children').length > 0,
						);
					}
					d.resolveWith(this, [category]);
				}, d.reject);

				return d.promise();
			},
			/**
			 * Destroy category
			 * @method
			 */
			remove: function (opts) {
				return this.deleteCategory(opts).then((uuid) =>
					window.Netx.eventProgressController
						.addEventProgress(uuid, {message: '', nonBlocking: true})
						.then(() => this),
				);
			},

			deleteCategory: ['deleteCategory', 'categoryid'],

			/**
			 * Update category
			 * @method
			 * @param {object} opts Request options
			 */
			update: function (opts) {
				return this.setCategoryObject(opts).then(() => {
					// Need the DAM to send full beans blah blah blah
					return this.fetch(opts);
				});
			},
			/**
			 * Fetch category children
			 * @method
			 */
			fetchChildren: ['getCategoryTree', 'id'],
			/**
			 * Fetch category breadcrumbs
			 * @method
			 * @param {object} opts Request options
			 */
			getCategoryBreadcrumbs: function (opts) {
				var cid = this.id;
				return ['getCategoryBreadcrumbs', cid > 1 ? cid : 0];
			},
			/**
			 * Fetch category by path string
			 * @method
			 * @param {object} opts Request options
			 */
			getCategoryByPath: function (opts) {
				var path = this.get('repositorydirectory').split('/');
				return ['getCategoryByPath', path];
			},
			/**
			 * Fetch category
			 * @method
			 * @param {object} opts Request options
			 */
			getCategoryData: function (opts) {
				var catId = this.id;
				if (catId == null) {
					return new $.Deferred().reject(
						new Error(
							'Skipping call to "getCategoryData". Category ID is null or undefined:',
							catId,
						),
					);
				}
				return ['getCategoryData', catId];
			},
			/**
			 * Fetches additional details about a category
			 * @method
			 */
			getCategoryDetailsData: ['getCategoryDetailsData', 'categoryid'],
			/**
			 * Create category
			 * @method
			 * @param {object} opts Request options
			 */
			setCategoryObject: function (opts) {
				return this._setCategoryObject(opts).then((uuid) =>
					pollUntil({uuid, throwOnWarnings: true}).then((progressReport) =>
						getObjectIdFromProgressReport(progressReport, true),
					),
				);
			},
			_setCategoryObject: function (opts) {
				return ['setCategoryObject', this.self()];
			},
		};
	}

	/**
	 * @method
	 * @param {object} data Data to parse
	 * @param {object} opts Options
	 */
	parseAndSave(data, opts) {
		// Use model's attribute keys to pull needed data out of `data` object
		const catData = _.pick.apply(
			_,
			[data].concat(Object.keys(this.attributes)),
		);

		const {attributeProfileId} = data;

		// If `attributeProfile` is null, then the user is removing the
		// attribute profile from the folder
		if (attributeProfileId === '') {
			this.selectedAttributeProfile = new AttributeProfile({profileId: null});
		} else {
			this.selectedAttributeProfile = new AttributeProfile({
				profileId: attributeProfileId,
			});
		}

		return this.save(catData, {wait: true, ...opts}).then(() => {
			// If attributeProfileId is undefined, we probably got here from creating
			// a folder where you cannot - currently - choose an attribute profile
			if (attributeProfileId === undefined) return;

			// If the attribute profile id is null, that means the user
			// removed the attribute profile from the folder
			if (this.selectedAttributeProfile.get('profileId') === null) {
				return this.selectedAttributeProfile.removeAttributeProfileFromFolder(
					this.id,
				);
			} else {
				return this.selectedAttributeProfile.setAttributeProfileForFolder(
					this.id,
				);
			}
		});
	}

	/**
	 * @property {object} parsers                         - Parsers for this model
	 * @property {method} parsers.getCategoryBreadcrumbs  - Parse fetch of category breadcrumbs
	 * @property {method} parsers.getCategoryByPath       - Parse fetch of category by path
	 * @property {method} parsers.getCategoryDetailsData  - Parse fetch of category details data
	 */
	get parsers() {
		return {
			/**
			 * Fetched category breadcrumbs
			 * @method
			 * @param {array} data Label value objects
			 * @param {object} opts Request options
			 */
			getCategoryBreadcrumbs: function (data, opts) {
				this.set('breadcrumb', _.toArray(data).reverse(), opts);
			},
			/**
			 * Fetched category by path
			 * @method
			 * @param {number} id Category id
			 * @param {object} opts Request options
			 * @fires module:category#category:current:update
			 * @todo Why do it with a dispatcher in the parser? Whatever called this can do that...
			 */
			getCategoryByPath: function (id, opts) {
				dispatcher.trigger('category:current:update', {categoryid: id});
				// TODO does getCategoryPath belong on the collection?
				// This parser wherever it winds up living needs to call this.fetch()
				// and add the result to the categories collection
			},
			/**
			 * Sets additional attributes about the category on the model
			 * @method
			 * @param {object} data Category details data
			 * @param {object} opts Request options
			 * @example {
			 *     "categoryId": 3,
			 *     "fileCount": 0,
			 *     "subCategoryCount": 0,
			 *     "byteCount":"0.0 bytes"
			 * }
			 */
			getCategoryDetailsData: function (data, opts) {
				// This call returns 'categoryId' instead of 'categoryid'.
				// Anyway, we know our id.  This call should not be allowed to change it.
				this.set(_.omit(data, 'categoryId', 'categoryid'), opts);
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Category attributes
	 * @param {object} opts Category options
	 */
	constructor(attrs, opts = {}) {
		// This is related to a circular reference somewhere
		// Anytime you find that a require at the top results in an empty object - this is why
		// https://stackoverflow.com/questions/37127083/can-two-files-have-each-other-as-dependencies-in-javascript-when-using-webpack
		// AssetModel = require('@netx/core-asset/models/asset').default;

		super(attrs, opts);

		// Debounce these methods
		this.assetsAddedToCategory = _.debounce(this.assetsAddedToCategory, 50);
		this.childAdded = _.debounce(this.childAdded, 100);
		this.childRemoved = _.debounce(this.childRemoved, 100);
		this.childrenChanged = _.debounce(this.childrenChanged, 100);
		this.close = _.debounce(this.close, 100);
		this.open = _.debounce(this.open, 100);

		// Member variable to hold up-to-date list of all attribute profiles
		this.attributeProfiles = new AttributeProfiles();

		// Need to defer so everything is done initing
		_.defer(
			_.bind(function () {
				// If a tree was not already set, try and set one
				if (!this.has('tree')) {
					this.set('tree', opts.tree || this.collection);
				}
				var tree = this.get('tree');
				if (tree) {
					// This is a bit backwards as we are storing the collection on the model and also belone to the collection we are storing
					// We always maintain a reference to our tree until we are totally removed
					// This way we do not errounously get garbage collected just because we left another collection (so long as we are in the tree)
					this.addCollectionReference(this.get('tree'));
				}
			}, this),
		);

		// Set childCategories collection
		if (!this.has('childCategories')) {
			this.set(
				'childCategories',
				new CategoryTreeNodesCollection(
					this.get('open') ? this.children() : [],
					{categoryid: this.id},
				),
			);
		}

		// Hey, listen!
		this.listenTo(
			this,
			'sync',
			_.debounce(function () {
				// If it was open, re-open
				this.isOpen() && this.open();
			}, 50),
		);

		// Child ids have changed
		this.listenTo(
			this,
			'change:children',
			_.debounce(function () {
				if (this.get('children').length === 0) {
					this.close();
				}
			}, 50),
		);

		// Category successfully edited/created
		this.listenTo(
			dispatcher,
			'category:edit:success',
			function (model, isNew, newParent) {
				if (model instanceof Category) {
					// Parent id of the updated category
					var parentId = parseInt(model.get('parentid'));
					if (newParent && parentId === customConfig.categoryid) {
						return;
					}
					// If a different model instance of the same DAM category has been edited, grab those changes.
					if (model.id == this.id && model !== this) {
						this.updated(model);
					}
					// Category successfully added to another - check if it was added to us
					else if (isNew && parentId == this.id) {
						this.childAdded(model);
					}
				}
			},
		);

		// The number of children in this category have changed
		this.listenTo(
			dispatcher,
			'category:children:changed',
			function (parentid, cat) {
				parentid = parseInt(parentid);
				// If parentid is "top level" - that should be sorted out by the tree collection
				if (parentid === this.id && parentid !== customConfig.categoryid) {
					this.childrenChanged({duplicateQueryPolicy: 'merge'});
				}
			},
		);

		// A child has been removed from category
		this.listenTo(
			dispatcher,
			'category:children:removed',
			function (parentid, cat) {
				parentid = parseInt(parentid);
				if (parentid === this.id && parentid !== customConfig.categoryid) {
					cat && this.childRemoved(cat.id, {duplicateQueryPolicy: 'merge'});
				}
			},
		);

		// Category successfully deleted
		this.listenTo(dispatcher, 'category:delete:success', function (cat) {
			// Check if it is one of our children
			if (this.get('children').indexOf(cat.id) > -1) {
				this.childRemoved(cat.id, {duplicateQueryPolicy: 'merge'});
			}
		});

		// Category was moved
		// We need to refetch as our ancestors, path and parentid have changed
		this.listenTo(
			dispatcher,
			'category:move:success',
			function (cat, oldParentId, newParentId) {
				// This should never happen - moving the "top level" category - but just in case
				if (cat && cat.id === this.id && cat.id !== customConfig.categoryid) {
					this.moved();
				}
			},
		);

		// Assets have been added to this category
		this.listenTo(
			dispatcher,
			CATEGORY_EVENT.ORGANIZE_ASSETS_SUCCESS,
			function (params) {
				if (params.action !== 'remove' && params.toCategoryId === this.id) {
					this.assetsAddedToCategory();
				}
			},
		);
	}

	/**
	 * A model attribute we are storing is not a subCollection but in this case is a collection reference - do not want to get those mixed up
	 * @override
	 */
	set() {
		// TODO: Fix this issue - this is dirty dirty
		return Backbone.ES6Model.prototype.set.apply(this, arguments);
	}
	/**
	 * Only garbage collect if we are no longer referenced in the tree
	 * @override
	 */
	garbageCollect() {
		var tree = this.get('tree');
		if (!tree || !tree.get(this.id) || tree.get(this.id) !== this) {
			super.garbageCollect();
		}
	}

	/**
	 * Fetches attribute profile name/ID for the attribute profile attached
	 * to the category
	 */
	getAttributeProfile() {
		return this.attributeProfiles
			.getAttributeProfileForFolder(this.id)
			.then(
				(attributeProfile) =>
					(this.selectedAttributeProfile = attributeProfile),
			);
	}

	/**
	 * @method
	 */
	assetsAddedToCategory() {
		// We can be pretty sure that we now have contents (if we did not already)
		this.set('hasContents', true);
	}

	/**
	 * Check our permissionMatrix for whether a particular named bit is set.
	 * @method
	 * @param {string} perm One of the permissions in {module:asset/properties/assetPermissions#matrixNames}
	 * @returns {mixed} returns true or false, or undefined (which is falsy) if the argument is undefined.
	 */
	// DEVNOTE:
	// Until we have a better include/export scheme, we won't know whether asset or category inits first,
	// so we can't define one of these in terms of the other at startup.
	//
	// So instead of doing this:
	//   allows: module:asset/models/asset.prototype.allows
	//
	// We must use a wrapper at run-time, like so:
	allows() {
		return AssetModel.prototype.allows.apply(this, arguments);
	}

	/**
	 * Child category added
	 * @method
	 * @param {Netx.Models.Category} cat Category
	 * @param {object} opts Request options
	 */
	childAdded(cat, opts) {
		this.fetch(_.extend({duplicateQueryPolicy: 'merge'}, opts));
	}
	/**
	 * Child removed/deleted
	 * @method
	 * @param {number} catid Category id
	 * @param {object} opts Request options
	 */
	childRemoved(catid, opts) {
		// One of our children was deleted!
		// 1) update fast!
		this.set({children: _.without(this.get('children'), catid)});
		// 2) sync any/all other changes from server.
		this.fetch(_.extend({duplicateQueryPolicy: 'merge'}, opts));
	}
	/**
	 * Category children have changed
	 * @method
	 * @param {object} opts Request options
	 */
	childrenChanged(opts) {
		// Let the tree handle this if there is one to be had
		this.fetch(opts).then(
			_.bind(function () {
				// Run through tree if we have one
				var tree = this.get('tree'),
					children = this.get('children'),
					fetchChildren = false;

				if (tree) {
					// Check if we have any of our children in the tree
					for (var i = 0, c = children.length; i < c; i++) {
						if (tree.get(children[i])) {
							fetchChildren = true;
							break;
						}
					}
					fetchChildren &&
						tree.fetchChildren(this.id).then(
							_.bind(function () {
								this.isOpen() && this.open();
							}, this),
						);
				}
			}, this),
		);
	}

	/**
	 * @method
	 * @return {Netx.Models.Category} Cloned category
	 */
	clone(tree) {
		tree = tree || this.get('tree');
		// Need this to be real clean
		return new this.constructor(
			_.extend({}, _.omit(this.attributes, 'childCategories', 'tree'), {
				childCategories: new CategoryTreeNodesCollection(
					this.get('childCategories').map(function (m) {
						return m.clone(tree);
					}, this),
					{categoryid: this.id},
				),
			}),
			{tree: tree},
		);
	}

	/**
	 * Close category
	 * Manage the state of this category in the category tree
	 * @method
	 * @param {object} opts Request options
	 */
	close(opts) {
		opts = opts || {};
		// Set on model
		this.set('open', false);

		// Deprecate - use change:open
		this.trigger('close', this);

		// Empty collection so our sub categories are not in the DOM
		this.get('childCategories').reset([]);

		var tree = this.get('tree');
		// Close all children if desired
		if (tree && opts.recursive) {
			_.each(
				this.get('children'),
				function (childId) {
					var child = tree.get(childId);
					child && child.close(opts);
				},
				this,
			);
		}
	}

	/**
	 * @method
	 * @return {number} Depth
	 */
	depth() {
		if (this.id == customConfig.categoryid) {
			return 0;
		}
		if (this.get('parentid') == customConfig.categoryid) {
			return 1;
		}

		var parentCat = this.parent();
		return !parentCat && this.get('ancestors')
			? this.get('ancestors').length || 1
			: parentCat.depth() + 1;
	}

	/*
	 * Queries the DAM for statistics about the category's contents.
	 * It returns a promise to categoryDetailsData model.
	 * @method
	 * @param {object} opts Fetch options
	 */
	getDetails(opts) {
		this.detailsData = this.detailsData || new CategoryDetailsData();
		this.detailsData.set({categoryId: this.id});
		return this.detailsData.fetch(opts);
	}

	/**
	 * Returns a direct link url
	 * @method
	 * @return {string} Direct link
	 */
	getDirectLink() {
		return [location.origin, location.pathname, '#category/', this.id].join('');
	}

	/**
	 * Query this category's position in the overall category tree structure
	 * @method
	 * @param {number} id Category id
	 * @param {boolean} Is child of
	 */
	isChildOf(id) {
		return this.get('parentid') == id;
	}

	/**
	 * @method
	 * @return {boolean} Open
	 */
	isOpen() {
		return this.get('open') ? true : false;
	}

	/**
	 * Query this category's position in the overall category tree structure
	 * @method
	 * @param {number} id Category id
	 * @return {boolean} Is parent of
	 */
	isParentOf(id) {
		return this.get('children').indexOf(id) > -1;
	}

	/**
	 * Move assets to this category
	 * @method
	 * @param {array} assets Asset ids
	 */
	moveToCategory(assets) {
		// TODO: make client model attribute
		this.assetsToTransfer = assets;
		return this._moveToCategory();
	}

	/**
	 * Our category was moved to another
	 * @method
	 */
	moved() {
		// Refetch us
		this.fetch({duplicateQueryPolicy: 'merge'}).then(
			_.bind(function () {
				// Run through tree
				var tree = this.get('tree'),
					children = this.get('children'),
					fetchChildren = false;

				if (tree) {
					for (var i = 0, c = children.length; i < c; i++) {
						if (tree.get(children[i])) {
							fetchChildren = true;
							break;
						}
					}
					fetchChildren &&
						tree.fetchChildren(this.id).then(
							_.bind(function () {
								this.isOpen() && this.open();
							}, this),
						);
				}
			}, this),
		);
	}

	/**
	 * Open category
	 * Manage the state of this category in the category tree
	 * @param {object} opts Request options
	 */
	open(opts) {
		opts = opts || {};

		// Set on model
		this.set('open', true);
		// Deprecate - use change:open
		this.trigger('open', this);

		let tree = this.get('tree');

		if (!tree && this.collection instanceof CategoryTreeNodesCollection) {
			tree = this.collection;
		}

		// Defer so that our views can respond to this change before we start shoving sub categories down its throat
		_.defer(() => {
			const childCategories = this.get('childCategories');
			const empty = !childCategories || childCategories.length === 0;
			let childrenUpdated = false;
			let p;

			// Set children if necessary
			if (empty) {
				p = this.children();
				p.then(function (children) {
					// Use reset or we get an ungdoly amount of add events in the child view code
					childCategories.reset(children);
					childrenUpdated = true;
				});
			} else {
				// Check that we differ at all - otherwise our child models should be updating to their own children changed events
				let diff = _.difference(
					this.get('children'),
					childCategories.pluck('categoryid'),
				);
				if (!diff.length) {
					diff = _.difference(
						childCategories.pluck('categoryid'),
						this.get('children'),
					);
				}
				if (diff.length) {
					p = this.children();
					p.then(function (children) {
						childCategories.set(children);
						childrenUpdated = true;
					});
				}
			}

			this.when(p).then(function () {
				// open any/all children (of this category) present in this.get('tree')
				if (tree && opts.recursive) {
					_.each(
						this.get('children'),
						function (childId) {
							var child = tree.get(childId);
							child && child.open(opts);
						},
						this,
					);
				} else {
					// If there are any children that should be open - open them again (basically put their childView collection models back)
					childCategories.each(function (cat) {
						cat.get('open') && cat.open();
					});
				}
			});
		});
	}

	/**
	 * List all (advanced search)
	 * @method
	 * @return {Netx.Collections.SearchRules_assets}
	 */
	searchRules_listAll() {
		// Build search rules
		var searchRules = new AssetSearhRulesCollection([
			{
				elementType: 23,
				elementValue1: this.id,
				elementSubType1: 0,
			},
		]);
		return searchRules;
	}

	/**
	 * Return an AssetSet model describing this category.
	 * @method
	 * @return {module:asset/models/assetSet} Asset set
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.category,
			ids: [this.id],
			models: [this],
			label: this.get('name'),
		});
	}

	/**
	 * Another instance of this category has been updated
	 * @method
	 * @param {Netx.Collections.Category} cat Category
	 */
	updated(cat) {
		// Do not include the childCategories reference - we will get a clean new one
		this.set(_.omit(cat.attributes, 'childCategories', 'tree'));
	}

	// children(), parent() and depth() presume that this.get('tree') is a general
	// object cache; that's true for the category tree objects, but not
	// true for a freshly created object.
	// TODO: implement a proper object cache, then query these through it.
	/**
	 * @method
	 * @return {array} Children
	 */
	children() {
		var tree = this.get('tree');

		// Tree safeguard
		if (!tree && this.collection instanceof CategoryTreeNodesCollection) {
			tree = this.collection;
		}

		var d = new $.Deferred(),
			self = this;

		if (!tree || !this.get('children').length) {
			d.resolve([]);
		} else {
			var children = _.compact(
				_.map(
					this.get('children'),
					function (catid) {
						return tree.get(catid);
					},
					this,
				),
			);

			d.resolve(children);
		}

		return d.promise();
	}

	/**
	 * @method
	 * @return {Netx.Models.Category} Parent
	 */
	parent() {
		var tree = this.get('tree');
		return tree ? tree.get(this.get('parentid')) : undefined;
	}

	/**
	 * @method
	 * @return {array} Parents
	 */
	parents(arr) {
		var tree = this.get('tree');

		arr = arr || [];

		var parentid = this.get('parentid');
		if (tree && parentid != customConfig.categoryid) {
			var parent = tree.get(parentid);
			arr.push(parent);
			return parent.parents(arr);
		}
		return arr;
	}

	/** @type {string} */
	static get __name__() {
		return 'Category';
	}
}
